import Icon from '@/components/ui/AppIcon';

interface HeroSectionProps {
  onGetStarted: () => void;
  onViewDocs: () => void;
}

export default function HeroSection({ onGetStarted, onViewDocs }: HeroSectionProps) {
  return (
    <section className="relative bg-gradient-to-br from-primary via-secondary to-ocean py-20 lg:py-28 overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="text-white space-y-6">
            <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
              <Icon name="SparklesIcon" size={20} className="text-accent" />
              <span className="text-sm font-medium">Trusted by 150+ Airlines Worldwide</span>
            </div>

            <h1 className="font-heading text-4xl lg:text-5xl xl:text-6xl font-bold leading-tight">
              Integrate with the Future of Travel Technology
            </h1>

            <p className="text-lg lg:text-xl text-white/90 leading-relaxed">
              Join our ecosystem of airline partners and travel agencies. Access powerful APIs, real-time PNR management, and revenue-sharing opportunities that transform your travel business.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <button
                onClick={onGetStarted}
                className="inline-flex items-center justify-center space-x-2 bg-coral hover:bg-[#FF5722] text-white px-8 py-4 rounded-lg font-heading font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <span>Start Integration</span>
                <Icon name="ArrowRightIcon" size={20} />
              </button>

              <button
                onClick={onViewDocs}
                className="inline-flex items-center justify-center space-x-2 bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white px-8 py-4 rounded-lg font-heading font-semibold text-lg border border-white/20 transition-all duration-300"
              >
                <Icon name="DocumentTextIcon" size={20} />
                <span>View Documentation</span>
              </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 pt-8 border-t border-white/20">
              <div>
                <div className="text-3xl font-heading font-bold text-accent">99.9%</div>
                <div className="text-sm text-white/80 mt-1">API Uptime</div>
              </div>
              <div>
                <div className="text-3xl font-heading font-bold text-accent">&lt;50ms</div>
                <div className="text-sm text-white/80 mt-1">Response Time</div>
              </div>
              <div>
                <div className="text-3xl font-heading font-bold text-accent">24/7</div>
                <div className="text-sm text-white/80 mt-1">Support</div>
              </div>
            </div>
          </div>

          {/* Right Content - API Preview */}
          <div className="relative">
            <div className="bg-charcoal rounded-xl shadow-2xl overflow-hidden border border-white/10">
              <div className="bg-[#1e1e1e] px-4 py-3 flex items-center space-x-2 border-b border-white/10">
                <div className="flex space-x-2">
                  <div className="w-3 h-3 rounded-full bg-error" />
                  <div className="w-3 h-3 rounded-full bg-warning" />
                  <div className="w-3 h-3 rounded-full bg-success" />
                </div>
                <span className="text-white/60 text-sm font-mono ml-4">api-explorer.js</span>
              </div>
              <div className="p-6 font-mono text-sm">
                <pre className="text-white/90 leading-relaxed">
                  <code>{`// Initialize Dialforflight API
const gateway = new Dialforflight({
  apiKey: 'your_api_key',
  environment: 'production'
});

// Create PNR with real-time confirmation
const booking = await gateway.createPNR({
  passengers: [...],
  flights: [...],
  payment: {...}
});

// Response: Active PNR in 50ms
console.log(booking.pnr); // "TG7X9K2M"
console.log(booking.status); // "CONFIRMED"`}</code>
                </pre>
              </div>
            </div>

            {/* Floating Badge */}
            <div className="absolute -bottom-4 -right-4 bg-success text-white px-6 py-3 rounded-lg shadow-xl">
              <div className="flex items-center space-x-2">
                <Icon name="CheckCircleIcon" size={24} />
                <div>
                  <div className="font-heading font-semibold">Live & Ready</div>
                  <div className="text-xs opacity-90">Production API Active</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}